/**
 * KARAKTER HESAPLAMA MOTORU
 *
 * `aggregateCharacter()` ham sheet verisini alir, hesaplanmis `ComputedSheet`
 * dondurur. TAMAMEN SAF ve DETERMINISTIK:
 *   - ayni girdi -> her zaman ayni cikti
 *   - veritabani, ag, tarih, Math.random() YOK
 *
 * Bu sayede ayni fonksiyon:
 *   - sunucuda (Worker) onceden hesaplayip saklamak icin
 *   - tarayicida anlik gosterge icin
 *   - testlerde beklenti dogrulamak icin
 * kullanilabilir.
 */

import {
  abilityModifier,
  allModifiers,
  averageHitDie,
  carryCapacity,
  proficiencyBonus,
} from './ability'
import { type MergedEffects, resolveChoices } from './choices'
import { combineConditions } from './conditions'
import {
  ABILITY_KEYS,
  type AbilityKey,
  type AbilityResult,
  type AppliedCondition,
  type AttackResult,
  type CharacterInput,
  type ComputedSheet,
  type ContentIndex,
  type FeatureEntry,
  type InventoryLine,
  type NaturalWeapon,
  type ResolvedFeature,
  type ResolvedPath,
  type ResourceDef,
  resolveResourceMax,
  SKILLS as SKILL_LIST,
  type SkillResult,
  type SpellcastingResult,
  type SpellSlotSummary,
} from './types'

const ATTUNEMENT_MAX_51 = 3

function modText(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`
}

// ---------------------------------------------------------------------------
// 1. Yetenek puanlari
// ---------------------------------------------------------------------------

function resolveAbilities(
  input: CharacterInput,
  content: ContentIndex,
  features: FeatureEntry[],
  warnings: string[],
): { scores: Record<AbilityKey, number>; featureBonuses: Partial<Record<AbilityKey, number>> } {
  const scores = { ...input.abilityScores }
  const featureBonuses: Partial<Record<AbilityKey, number>> = {}

  const race = input.raceId ? content.races[input.raceId] : undefined
  if (input.raceId && !race) warnings.push(`Irk bulunamadi: ${input.raceId}`)
  if (race) {
    for (const key of ABILITY_KEYS) {
      const bonus = race.abilityBonuses[key]
      if (bonus) scores[key] = (scores[key] ?? 10) + bonus
    }
  }

  // Oyuncunun sectigi bonuslar (Irk varyanti, ASI'lar)
  for (const key of ABILITY_KEYS) {
    const bonus = input.asiBonuses?.[key]
    if (bonus) scores[key] = (scores[key] ?? 10) + bonus
  }

  for (const feature of features) {
    if (!feature.abilityBonuses) continue
    for (const key of ABILITY_KEYS) {
      const bonus = feature.abilityBonuses[key]
      if (!bonus) continue
      scores[key] = (scores[key] ?? 10) + bonus
      featureBonuses[key] = (featureBonuses[key] ?? 0) + bonus
    }
  }

  for (const key of ABILITY_KEYS) {
    if (scores[key] == null) {
      warnings.push(`${key} puani tanimsiz, 10 varsayildi.`)
      scores[key] = 10
    }
  }

  return { scores, featureBonuses }
}

// ---------------------------------------------------------------------------
// 2. Ozellikler (features) toplama
// ---------------------------------------------------------------------------

interface GatheredFeatures {
  features: FeatureEntry[]
  resolved: ResolvedFeature[]
  asiEarned: number
  spellSlotsOverride: Record<string, number>
  grantedProficiencies: Set<string>
  armorProficiencies: Set<string>
  weaponProficiencies: Set<string>
  savingThrowProficiencies: Set<AbilityKey>
  defenseMode: 'unarmoredDex' | 'unarmoredDexCon' | null
  /**
   * Her AC bonusu AYRI bir kaynak olarak tutulur; her birinin kendi tavanı ve
   * kendi "zırh gerekli" işareti vardır. Toplam bir sayıya indirgemek
   * "hangi bonus uygulanmalı?" sorusunu cevaplamayı imkânsızlaştırır.
   */
  acBonusSources: { value: number; cap: number; requiresArmor: boolean }[]
  /** Alinan ozel yollar (sheet'te "Yollarim" bolumu bunu cizer). */
  paths: ResolvedPath[]
  /** Yollarin verdigi ozel kaynaklar (KP vb). */
  resources: ResourceDef[]
}

function gatherFeatures(
  input: CharacterInput,
  content: ContentIndex,
  warnings: string[],
): GatheredFeatures {
  const out: GatheredFeatures = {
    features: [],
    resolved: [],
    asiEarned: 0,
    spellSlotsOverride: {},
    grantedProficiencies: new Set(),
    armorProficiencies: new Set(),
    weaponProficiencies: new Set(),
    savingThrowProficiencies: new Set(),
    defenseMode: null,
    acBonusSources: [],
    paths: [],
    resources: [],
  }

  const addFeature = (
    feature: FeatureEntry,
    source: ResolvedFeature['source'],
    sourceName: string,
    level?: number,
  ) => {
    out.features.push(feature)
    out.resolved.push({ id: feature.id, name: feature.name, source, sourceName, level })

    if (feature.grantsProficiencies) {
      for (const p of feature.grantsProficiencies) out.grantedProficiencies.add(p)
    }
    if (feature.savingThrowProficiencies) {
      for (const s of feature.savingThrowProficiencies) out.savingThrowProficiencies.add(s)
    }
    if (feature.spellSlotsOverride) {
      for (const [slotLevel, count] of Object.entries(feature.spellSlotsOverride)) {
        out.spellSlotsOverride[slotLevel] = Math.max(out.spellSlotsOverride[slotLevel] ?? 0, count)
      }
    }
    if (feature.acBonus) {
      out.acBonusSources.push({
        value: feature.acBonus,
        cap: feature.acBonusCap ?? feature.acBonus,
        requiresArmor: feature.requiresArmor === true,
      })
    }
    if (feature.defenseMode) out.defenseMode = feature.defenseMode
  }

  // --- Siniflar (coklu sinif destegi) ---
  for (const assignment of input.classes) {
    const cls = content.classes[assignment.classId]
    if (!cls) {
      warnings.push(`Sinif bulunamadi: ${assignment.classId}`)
      continue
    }

    for (const prof of cls.savingThrowProficiencies) out.savingThrowProficiencies.add(prof)
    for (const prof of cls.armorProficiencies) out.armorProficiencies.add(prof)
    for (const prof of cls.weaponProficiencies) out.weaponProficiencies.add(prof)

    const sorted = [...cls.levels].sort((a, b) => a.level - b.level)
    for (const tier of sorted) {
      if (tier.level > assignment.level) break
      out.asiEarned += tier.asi
      for (const feature of tier.features) addFeature(feature, 'class', cls.name, tier.level)
    }
  }

  // --- Irk ---
  // ONEMLI: Irk ozelliklerinin cogu SEVIYE KAPILI (3/5/7/9/13/17).
  // `grantedAtLevel` degeri karakterin seviyesini asmiyorsa ozellige
  // UYGULANMAZ — aksi halde 1. seviyede Ejder Soylu 7. seviye ozelligini alirdi.
  if (input.raceId) {
    const race = content.races[input.raceId]
    if (race) {
      out.asiEarned += race.asi
      for (const feature of race.features) {
        const at = feature.grantedAtLevel ?? 1
        if (at <= totalLevelOf(input)) {
          addFeature(feature, 'race', race.name, at)
        }
      }
    }
  }

  // --- Arka plan ---
  if (input.backgroundId) {
    const bg = content.backgrounds[input.backgroundId]
    if (bg) {
      for (const p of bg.skillProficiencies) out.grantedProficiencies.add(p)
      for (const p of bg.toolProficiencies) out.grantedProficiencies.add(p)
      for (const feature of bg.features) addFeature(feature, 'background', bg.name, 1)
    }
  } else {
    warnings.push('Arka plan secilmedi.')
  }

  // --- Feat'ler (ozellikler) ---
  for (const feat of Object.values(content.features ?? {})) {
    if (!input.proficiencies.includes(`feat.${feat.id}`)) continue
    addFeature(feat, 'feat', feat.name)
  }

  // --- Ozel yollar ---
  // Yollar KATMAN'dir: sirayla islenir, hicbiri digerinin yerine gecmez.
  // Ayni kaynagi veren iki yol birlestirilir (azami deger + ilk tanim).
  // Bilinmeyen yol kimligi SESSIZCE yutulmaz, uyari uretir — karakter
  // sahibi silinmis bir yola sahip olabilir ve bu veri kaybi demektir.
  const level = totalLevelOf(input)
  const seenPathIds = new Set<string>()
  for (const pathId of input.paths ?? []) {
    if (seenPathIds.has(pathId)) {
      warnings.push(`Yol birden fazla eklenmis, atlandi: ${pathId}`)
      continue
    }
    seenPathIds.add(pathId)

    const path = content.paths?.[pathId]
    if (!path) {
      warnings.push(`Ozel yol bulunamadi: ${pathId}`)
      continue
    }

    const summary = out.paths.find((p) => p.id === path.id)
    if (!summary) {
      out.paths.push({
        id: path.id,
        name: path.name,
        description: path.description,
        allFeatures: path.features.map((f) => ({
          id: f.id,
          name: f.name,
          grantedAtLevel: f.grantedAtLevel ?? 1,
        })),
        activeFeatures: [],
        lockedFeatures: [],
        resources: path.resources ?? [],
      })
    } else {
      // Ayni yol iki kez geldiyse ozelliklerini birlestir.
      summary.allFeatures.push(
        ...path.features.map((f) => ({
          id: f.id,
          name: f.name,
          grantedAtLevel: f.grantedAtLevel ?? 1,
        })),
      )
    }

    for (const resource of path.resources ?? []) {
      const existing = out.resources.find((r) => r.id === resource.id)
      if (!existing) {
        out.resources.push(resource)
        continue
      }
      // Ayni kaynak iki yoldan geldi: tanimlari BIRLESTIR.
      //
      // Sabit `max` icin buyuk olan kazanir. `scaling` icin kural, kaynagin
      // hangi seviyede BASLADIGI daha erken olan secilir — cunku "once sahip
      // ol, sonra buyu" mantigi dogru olan: 3. seviyede acilan bir KP, 5.
      // seviyede acilan bir KP ile birlestirilirse 3.'te baslamalidir.
      if (existing.max != null && resource.max != null) {
        existing.max = Math.max(existing.max, resource.max)
      }
      if (existing.scaling && resource.scaling) {
        existing.scaling = {
          ...resource.scaling,
          startsAtLevel: Math.min(existing.scaling.startsAtLevel, resource.scaling.startsAtLevel),
          baseMax: Math.max(existing.scaling.baseMax, resource.scaling.baseMax),
        }
      } else if (resource.scaling) {
        existing.scaling = resource.scaling
      }
    }

    // Seviye kapisi: 3. seviyede acilan yol ozelligi 1. seviyede UYGULANMAZ.
    for (const feature of path.features) {
      const at = feature.grantedAtLevel ?? 1
      if (at <= level) addFeature(feature, 'path', path.name, at)
    }
  }

  // Ozet bosluklarini doldur: aktif olanlar / henuz acilmamis olanlar.
  for (const summary of out.paths) {
    for (const f of summary.allFeatures) {
      if (f.grantedAtLevel <= level) summary.activeFeatures.push(f)
      else summary.lockedFeatures.push(f)
    }
  }

  return out
}

/** Birlestirilmis secim etkilerini tek bir `FeatureEntry` sekline indirger. */
function applyChoiceEffects(target: FeatureEntry, merged: MergedEffects): void {
  if (Object.keys(merged.abilityBonuses).length) target.abilityBonuses = merged.abilityBonuses
  if (merged.damageResistances.length) target.damageResistances = merged.damageResistances
  if (merged.damageImmunities.length) target.damageImmunities = merged.damageImmunities
  if (merged.damageVulnerabilities.length)
    target.damageVulnerabilities = merged.damageVulnerabilities
  if (merged.conditionImmunities.length) target.conditionImmunities = merged.conditionImmunities
  if (merged.cantrips.length)
    target.grantsProficiencies = merged.cantrips.map((c) => `cantrip.${c}`)
  if (merged.innateSpells.length) target.innateSpells = merged.innateSpells
  if (merged.spellSlotsOverride && Object.keys(merged.spellSlotsOverride).length) {
    target.spellSlotsOverride = merged.spellSlotsOverride
  }
}

/**
 * Dogal silahlari (pençe, bot, boynuz) saldiri satirlarina cevirir.
 * `minimumDice` uygulanir: hasar zari "en az" bu deger olur.
 */
function buildNaturalWeapons(
  weapons: readonly NaturalWeapon[],
  modifiers: Record<AbilityKey, number>,
  pb: number,
): AttackResult[] {
  return weapons.map((w, index) => {
    const statMod = w.finesse ? Math.max(modifiers.str, modifiers.dex) : modifiers[w.ability]
    const dice = w.minimumDice ? strongerDice(w.dice, w.minimumDice) : w.dice
    return {
      itemId: `natural-${index}-${w.name}`,
      name: w.name,
      isRanged: false,
      rangeShort: 5,
      rangeLong: null,
      attackBonus: statMod + pb,
      damageDice: dice,
      damageBonus: statMod,
      damageType: w.damageType,
      isFinesse: w.finesse === true,
      isVersatile: false,
      versatileDamageDice: null,
      notes: w.minimumDice ? [`asgari zar: ${w.minimumDice}`] : [],
    }
  })
}

/**
 * Iki zar arasindan daha GUCLU olani secer ("en az 1d8" kurali).
 * Karsilastirma beklenen deger uzerinden: count * (sides + 1) / 2.
 */
export function strongerDice(a: string, b: string): string {
  const expected = (dice: string): number => {
    const m = /(\d*)d(\d+)/.exec(dice)
    if (!m) return 0
    const count = Number(m[1] || '1')
    const sides = Number(m[2])
    return (count * (sides + 1)) / 2
  }
  return expected(a) >= expected(b) ? a : b
}

function totalLevelOf(input: CharacterInput): number {
  return Math.max(
    1,
    input.classes.reduce((sum, c) => sum + c.level, 0),
  )
}

// ---------------------------------------------------------------------------
// 3. Zirh sinifi (AC)
// ---------------------------------------------------------------------------

function computeArmorClass(
  input: CharacterInput,
  content: ContentIndex,
  scores: Record<AbilityKey, number>,
  gathered: GatheredFeatures,
  warnings: string[],
  acBase = 10,
): { ac: number; equippedArmor: string | null; dexAcCap: number | null } {
  const dexMod = abilityModifier(scores.dex)
  const conMod = abilityModifier(scores.con)

  const armorLine = input.inventory.find((slot) => content.items[slot.itemId]?.armorType != null)
  const shieldLine = input.inventory.find((slot) => content.items[slot.itemId]?.shieldBonus != null)

  let equippedArmor: string | null = null
  let dexAcCap: number | null = null
  let dexContribution = dexMod
  let base = acBase
  let usingArmor = false

  if (armorLine) {
    const armor = content.items[armorLine.itemId]!
    const armorType = armor.armorType!
    const proficient = gathered.armorProficiencies.has(armorType)
    const strOk = armor.strengthRequirement == null || scores.str >= armor.strengthRequirement
    const bonus = Math.min(armor.armorBonus ?? 0, armor.maxBonus ?? Number.POSITIVE_INFINITY)

    if (armorType === 'heavy' && !proficient) {
      warnings.push(
        `${armor.name}: ağır zırh yetkinliğin yok, zırh kullanılamıyor — zırhsız sayıldın.`,
      )
    } else {
      usingArmor = true
      equippedArmor = armor.name
      base = (armor.armorClass ?? 10) + bonus

      if (!strOk) {
        warnings.push(
          `${armor.name} için GÜÇ ${armor.strengthRequirement} gerekiyor; kaldırılamadığı için Çeviklik katkısı sıfırlandı.`,
        )
        dexContribution = 0
      }

      if (armorType === 'heavy') {
        dexContribution = 0
      } else if (armorType === 'medium') {
        if (proficient) {
          dexAcCap = armorLine.dexAcCap ?? 2
        } else {
          warnings.push(
            `${armor.name}: orta zırh yetkinliğin yok, Çeviklik bonusunun tamamı eklendi.`,
          )
        }
      }
      // light: tam Çeviklik, ek kisit yok

      if (dexAcCap != null) dexContribution = Math.min(dexContribution, dexAcCap)
    }
  }

  let ac = base + dexContribution

  // Zırh yoksa: Unarmored Defense (barbarian vb.)
  if (!usingArmor && gathered.defenseMode === 'unarmoredDexCon') {
    ac += conMod
  }

  // --- Kalkan ---
  if (shieldLine) {
    const shield = content.items[shieldLine.itemId]!
    if (gathered.armorProficiencies.has('shields')) {
      ac += shield.shieldBonus ?? 2
    } else {
      warnings.push(`${shield.name}: kalkan yetkinliğin yok, bonus uygulanmadı.`)
    }
  }

  // --- Ozellik bonuslari ---
  // Her kaynak ayri ele alinir: tavana kirpilir ve "zırh gerekli" olanlar
  // zırhsız hesapta ATLANIR (2024 Silah Ustalığı "Savunma" kuralı).
  //
  // Atlanan bonus icin UYARI URETILMEZ: zırhsız bir Paladin'de bu beklenen
  // durumdur, veri hatası değildir. Gürültü yaratmak gerçek hataları maskeler.
  for (const source of gathered.acBonusSources) {
    if (source.requiresArmor && !usingArmor) continue
    ac += Math.min(source.value, source.cap)
  }
  // --- Eşya bonuslari ---
  for (const slot of input.inventory) {
    for (const bonus of content.items[slot.itemId]?.bonuses ?? []) {
      ac += bonus.armorClass ?? 0
    }
  }

  return { ac, equippedArmor, dexAcCap }
}

// ---------------------------------------------------------------------------
// 4. Can puani (HP)
// ---------------------------------------------------------------------------

/**
 * 5e "fixed" HP yontemi (D&D Beyond ayari):
 *   - 1. seviye  : sinifin TAM can zari  + CON mod   (d10 Paladin icin 10)
 *   - 2. seviye+ : can zarinin ORTALAMASI + CON mod  (d10 icin 6)
 *
 * Bu ayrım onemlidir: ortalama her seviyeye uygulansydi 1. seviye
 * paladin 7 HP ile baslardi (dogru deger 11).
 */
function computeHitPoints(
  input: CharacterInput,
  content: ContentIndex,
  conMod: number,
  warnings: string[],
): { maxHp: number; hitDice: string } {
  let total = 0
  const diceParts: string[] = []

  for (const assignment of input.classes) {
    const cls = content.classes[assignment.classId]
    if (!cls) continue

    if (assignment.hitPointsOverride != null) {
      total += assignment.hitPointsOverride
      diceParts.push(`${assignment.level}d${cls.hitDie} (elle)`)
      continue
    }

    for (let lvl = 1; lvl <= assignment.level; lvl++) {
      const dieValue = lvl === 1 ? cls.hitDie : averageHitDie(cls.hitDie)
      total += dieValue + conMod
    }
    diceParts.push(`${assignment.level}d${cls.hitDie}`)
  }

  if (total <= 0) warnings.push('Can puani hesaplanamadi: sinif atanmamis olabilir.')

  return { maxHp: Math.max(0, total), hitDice: diceParts.join(' + ') || '—' }
}

// ---------------------------------------------------------------------------
// 5. Büyücülük
// ---------------------------------------------------------------------------

function computeSpellcasting(
  input: CharacterInput,
  content: ContentIndex,
  gathered: GatheredFeatures,
  pb: number,
  scores: Record<AbilityKey, number>,
  warnings: string[],
): SpellcastingResult {
  // En yuksek seviyeli buyucu sinif (5e'de coklu sinifte yuvalar tek siniften gelir)
  const caster = [...input.classes]
    .sort((a, b) => b.level - a.level)
    .map((a) => ({ assignment: a, cls: content.classes[a.classId] }))
    .find((c) => c.cls && c.cls.spellcasting.mode !== 'none')

  if (!caster) {
    return {
      ability: 'int',
      abilityModifier: 0,
      saveDc: 0,
      attackBonus: 0,
      preparation: 'prepared',
      cantripsKnown: 0,
      preparedCount: 0,
      slots: [],
      isSpellcaster: false,
    }
  }

  const { assignment, cls } = caster
  const spec = cls!.spellcasting
  if (spec.mode === 'none') {
    return {
      ability: 'int',
      abilityModifier: 0,
      saveDc: 0,
      attackBonus: 0,
      preparation: 'prepared',
      cantripsKnown: 0,
      preparedCount: 0,
      slots: [],
      isSpellcaster: false,
    }
  }

  const ability = spec.ability
  const abilityMod = abilityModifier(scores[ability])
  const casterLevel = assignment.level

  // Cantrip sayisi
  let cantripsKnown = 0
  for (const step of spec.cantripProgression) {
    if (casterLevel >= step.level) cantripsKnown = step.count
  }

  // Hazirlanacak buyu sayisi
  let preparedCount = 0
  if (spec.preparation === 'known') {
    preparedCount = casterLevel
  } else if (spec.mode === 'artificer') {
    preparedCount = Math.max(2, casterLevel - abilityModifier(scores.dex))
  } else if (spec.mode === 'half') {
    preparedCount = Math.ceil(casterLevel / 2)
  } else {
    preparedCount = casterLevel
  }

  // Slot tablosu: sinif seviyelerinden + ozellik override
  const slotTotals: Record<string, number> = {}
  const tiers = [...cls!.levels].sort((a, b) => a.level - b.level)
  for (const tier of tiers) {
    if (tier.level > casterLevel) break
    for (const [slotLevel, count] of Object.entries(tier.spellSlots)) {
      slotTotals[slotLevel] = count
    }
  }
  for (const [slotLevel, count] of Object.entries(gathered.spellSlotsOverride)) {
    slotTotals[slotLevel] = Math.max(slotTotals[slotLevel] ?? 0, count)
  }

  const slots: SpellSlotSummary[] = Object.entries(slotTotals)
    .filter(([, count]) => count > 0)
    .map(([slotLevel, total]) => ({
      level: Number(slotLevel),
      total,
      used: 0,
      remaining: total,
    }))
    .sort((a, b) => a.level - b.level)

  if (slots.length === 0) warnings.push(`${cls!.name} icin slot tablosu bulunamadi.`)

  return {
    ability,
    abilityModifier: abilityMod,
    saveDc: 8 + pb + abilityMod,
    attackBonus: pb + abilityMod,
    preparation: spec.preparation,
    cantripsKnown,
    preparedCount,
    slots,
    isSpellcaster: true,
  }
}

// ---------------------------------------------------------------------------
// 6. Envanter, saldiri, agirlik
// ---------------------------------------------------------------------------

function buildInventory(
  input: CharacterInput,
  content: ContentIndex,
): { lines: InventoryLine[]; weight: number } {
  const lines: InventoryLine[] = []
  let totalWeight = 0

  for (const slot of input.inventory) {
    const item = content.items[slot.itemId]
    if (!item) continue
    const weightEach = item.weight ?? 0
    totalWeight += weightEach * slot.quantity
    lines.push({
      itemId: item.id,
      name: item.name,
      quantity: slot.quantity,
      weightEach,
      requiresAttunement: item.requiresAttunement,
      attuned: slot.attuned,
      equipped: slot.equipSlot != null,
      slot: slot.equipSlot ?? null,
      chargesUsed: slot.chargesUsed ?? 0,
      chargesMax: item.chargesMax ?? 0,
    })
  }

  return { lines, weight: totalWeight }
}

function buildAttacks(
  input: CharacterInput,
  content: ContentIndex,
  modifiers: Record<AbilityKey, number>,
  pb: number,
  gathered: GatheredFeatures,
): AttackResult[] {
  const attacks: AttackResult[] = []
  const dexMod = modifiers.dex
  const strMod = modifiers.str
  const notes: string[] = []

  // Ozellik kaynakli bonuslar (orn. Cra: ilk ranged atista +1)
  for (const feature of gathered.features) {
    if (feature.skillBonuses) {
      for (const [key, value] of Object.entries(feature.skillBonuses)) {
        if (key.startsWith('attack.')) notes.push(`${feature.name}: +${value}`)
      }
    }
  }

  for (const slot of input.inventory) {
    const item = content.items[slot.itemId]
    if (!item?.weapon) continue
    const w = item.weapon

    // Cevik (finesse) silahlar en iyi modifikatoru secer
    const statMod = w.isFinesse ? Math.max(strMod, dexMod) : strMod

    let attackBonus = statMod + pb
    let damageBonus = statMod

    for (const bonus of item.bonuses ?? []) {
      attackBonus += bonus.ability?.str ?? 0
      damageBonus += bonus.ability?.str ?? 0
    }

    attacks.push({
      itemId: item.id,
      name: item.name,
      isRanged: w.thrown === true || /bow|arrow|javelin|atış|ok/i.test(item.name),
      rangeShort: w.thrown ? 20 : 5,
      rangeLong: w.thrown ? 60 : (w.reach ?? 5),
      attackBonus,
      damageDice: w.damage,
      damageBonus,
      damageType: w.damageType,
      isFinesse: w.isFinesse === true,
      isVersatile: w.isVersatile === true,
      versatileDamageDice: w.versatileDamage ?? null,
      notes: notes.filter(Boolean),
    })
  }

  return attacks
}

// ---------------------------------------------------------------------------
// ANA GIRIS NOKTASI
// ---------------------------------------------------------------------------

/**
 * Karakterin hesaplanmis kagit bilgisini uretir.
 * @param input  Ham karakter verisi
 * @param content Irk/sinif/arka plan/esya tanimlari
 */
export function aggregateCharacter(input: CharacterInput, content: ContentIndex): ComputedSheet {
  const warnings: string[] = []

  // --- Seviye ---
  // TEK KAYNAK: sinif atamalarının toplamı. `input.level` yalnızca bildirim
  // amaçlıdır; iki farklı sayı olursa veri hatasıdır ve UYARI üretilir.
  // (Çoklu sınıfta seviye sınıflardan türetilmek zorundadır.)
  const level = Math.max(
    1,
    input.classes.reduce((sum, c) => sum + c.level, 0),
  )
  if (input.level != null && input.level !== level) {
    warnings.push(
      `Kaydedilmiş seviye (${input.level}) siniflardan hesaplanan seviyeyle (${level}) uyuşmuyor. Siniflar esas alındı.`,
    )
  }
  const pb = proficiencyBonus(level)

  const gathered = gatherFeatures(input, content, warnings)

  // --- Irk ve secimler ---
  // SIRA ONEMLI: secimler ONCE cozulur, cunku secimler yetenek bonusu verebilir
  // (orn. Mutant: "Agir Yapi" +2 Dayanıklılık). `resolveAbilities` secim
  // ozetini gormek ZORUNDA, yoksa secim bonuslari hicbir zaman uygulanmaz.
  const race = input.raceId ? content.races[input.raceId] : undefined
  const choices = resolveChoices(race?.choices ?? [], input.choices, level)
  warnings.push(...choices.warnings)

  // Secim etkilerini toplu olarak islenen bir "ozet" ozellige cevir; boylece
  // asagidaki hesaplar (AC, direnc, hiz, yetenek) tek kaynaktan beslenir.
  const choiceFeature: FeatureEntry = { id: 'race-choices', name: 'Irk Seçimleri' }
  applyChoiceEffects(choiceFeature, choices.merged)
  gathered.features.push(choiceFeature)
  if (choices.merged.acBonus) {
    gathered.acBonusSources.push({
      value: choices.merged.acBonus,
      cap: choices.merged.acBonus,
      requiresArmor: false,
    })
  }
  for (const p of choices.merged.savingThrowProficiencies) {
    gathered.savingThrowProficiencies.add(p)
  }

  // Secimlerin verdigi feat'ler (orn. İnsan → 1 Origin Feat) sınıf özellikleri gibi
  // ayri bir turden geçer.
  const featIdsFromChoices = choices.merged.feats.filter(
    (id) => !input.proficiencies.includes(`feat.${id}`),
  )
  if (featIdsFromChoices.length > 0) {
    input = {
      ...input,
      proficiencies: [...input.proficiencies, ...featIdsFromChoices.map((id) => `feat.${id}`)],
    }
    // `gatherFeatures` icin yeniden topla (feat'ler icin)
    const refetched = gatherFeatures(input, content, [])
    gathered.features.push(...refetched.features.filter((f) => f.id.startsWith('feat')))
    gathered.resolved.push(...refetched.resolved)
  }

  // --- Yetenekler (secim bonuslari dahil) ---
  const { scores } = resolveAbilities(input, content, gathered.features, warnings)
  const modifiers = allModifiers(scores)

  // --- Kurtarma atislari ---
  const savingThrows = {} as Record<AbilityKey, number>
  const abilities = {} as Record<AbilityKey, AbilityResult>
  for (const key of ABILITY_KEYS) {
    const saveProficient = gathered.savingThrowProficiencies.has(key)
    const saveBonus = modifiers[key] + (saveProficient ? pb : 0)
    savingThrows[key] = saveBonus
    abilities[key] = {
      score: scores[key],
      modifier: modifiers[key],
      saveBonus,
      proficient: saveProficient,
    }
  }

  // --- Beceriler ---
  const proficiencySet = new Set<string>([
    ...input.proficiencies,
    ...gathered.grantedProficiencies,
    ...choices.merged.skillProficiencies,
    ...choices.merged.grantsProficiencies,
  ])
  const skills: SkillResult[] = SKILL_LIST.map((skill) => {
    const mod = modifiers[skill.ability]
    const proficient = proficiencySet.has(`skill.${skill.key}`) || proficiencySet.has(skill.key)
    const bonus = mod + (proficient ? pb : 0)
    return {
      key: skill.key,
      label: skill.label,
      labelTr: skill.labelTr,
      ability: skill.ability,
      proficient,
      modifier: bonus,
      passive: 10 + bonus,
    }
  })
  const passiveOf = (key: string) => skills.find((s) => s.key === key)?.passive ?? 10

  // --- Savunma ---
  const acBase = choices.merged.acBase ?? race?.acBase ?? 10
  const { ac, equippedArmor, dexAcCap } = computeArmorClass(
    input,
    content,
    scores,
    gathered,
    warnings,
    acBase,
  )
  const { maxHp } = computeHitPoints(input, content, modifiers.con, warnings)

  // --- Hız ---
  const speed = (race?.speed ?? 30) + choices.merged.speedBonus

  // --- Büyücülük ---
  const spellcasting = computeSpellcasting(input, content, gathered, pb, scores, warnings)

  // --- Dayanıklılık / direncler ---
  const damageResistances = new Set<string>()
  const damageImmunities = new Set<string>()
  const damageVulnerabilities = new Set<string>()
  const conditionImmunities = new Set<string>()
  for (const feature of gathered.features) {
    for (const r of feature.damageResistances ?? []) damageResistances.add(r.toLowerCase())
    for (const i of feature.damageImmunities ?? []) damageImmunities.add(i.toLowerCase())
    for (const v of feature.damageVulnerabilities ?? []) damageVulnerabilities.add(v.toLowerCase())
    for (const c of feature.conditionImmunities ?? []) conditionImmunities.add(c.toLowerCase())
  }

  // --- Odaklanma (attunement) ---
  const attuned = input.inventory.filter(
    (slot) => content.items[slot.itemId]?.requiresAttunement && slot.attuned,
  ).length
  if (attuned > ATTUNEMENT_MAX_51) {
    warnings.push(`Odaklanma siniri asildi (${attuned}/${ATTUNEMENT_MAX_51}).`)
  }

  // --- Envanter & saldiri ---
  const { lines, weight } = buildInventory(input, content)
  const attacks = buildAttacks(input, content, modifiers, pb, gathered)

  // --- Dogal silahlar (irk secimlerinden veya ozelliklerden) ---
  const naturalWeapons: NaturalWeapon[] = [...choices.merged.naturalWeapons]
  for (const feature of gathered.features) {
    if (feature.naturalWeapon) naturalWeapons.push(feature.naturalWeapon)
  }
  const naturalAttacks = buildNaturalWeapons(naturalWeapons, modifiers, pb)

  // --- Yuva harcamadan kullanilabilen buyuler ---
  const innateSpells = [
    ...(race?.innateSpells ?? []),
    ...choices.merged.innateSpells,
    ...gathered.features.flatMap((f) => f.innateSpells ?? []),
  ]
    .filter((grant) => grant.grantedAtLevel <= level)
    .map((grant) => ({
      spellIds: [...grant.spellIds],
      grantedAtLevel: grant.grantedAtLevel,
      uses: { ...grant.uses },
    }))

  // --- Kosullar ---
  const conditionResult = combineConditions(input.conditions)
  for (const unknown of conditionResult.unknown) {
    warnings.push(`Bilinmeyen kosul: "${unknown}" (kural motoru bunu yorumlayamadi).`)
  }
  const conditions: AppliedCondition[] = conditionResult.conditions.map((c) => ({
    slug: c.slug,
    label: c.label,
    labelTr: c.labelTr,
    canMove: c.canMove,
    canSpeak: c.canSpeak,
    canSee: c.canSee,
    canAct: c.canAct,
    canAttack: c.canAttack,
    incomingAttack: c.incomingAttack,
    ownAttack: c.ownAttack,
  }))

  // --- Inisiyatif & pasifler ---
  let initiative = modifiers.dex
  for (const slot of input.inventory) {
    for (const bonus of content.items[slot.itemId]?.bonuses ?? []) {
      initiative += bonus.initiative ?? 0
    }
  }

  return {
    ruleset: input.ruleset,
    level,
    xp: input.xp ?? 0,
    proficiencyBonus: pb,
    asiEarned: gathered.asiEarned,
    asiChoicesRemaining: gathered.asiEarned,
    abilities,
    skills,
    savingThrows,
    defense: {
      armorClass: ac,
      initiative,
      speed,
      hitPoints: maxHp,
      tempHitPoints: input.tempHitPoints ?? 0,
      carryCapacity: carryCapacity(scores.str),
      equippedArmor,
      dexAcCap,
      acBase,
    },
    spellcasting,
    attackBonus: modifiers.cha + pb,
    passivePerception: passiveOf('perception'),
    passiveInvestigation: passiveOf('investigation'),
    passiveInsight: passiveOf('insight'),
    senses: {
      darkvision: Math.max(0, race?.darkvision ?? 0, choices.merged.darkvision ?? 0),
    },
    resistances: {
      damageResistances: [...damageResistances].sort(),
      damageImmunities: [...damageImmunities].sort(),
      damageVulnerabilities: [...damageVulnerabilities].sort(),
      conditionImmunities: [...conditionImmunities].sort(),
    },
    attunement: { used: attuned, max: ATTUNEMENT_MAX_51 },
    features: gathered.resolved,
    attacks,
    naturalWeapons: naturalAttacks,
    innateSpells,
    choices: choices.resolved,
    paths: gathered.paths,
    // Yollarin KP'si SEVIYEYE BAGLI: kural esigine gelmeden "yok"tir, 0 degil.
    // Uzerine GM'in opsiyonel bonusu eklenir.
    //
    //   azami = seviyeye gore taban + input.resourceBonuses[resource.id]
    //
    // Bonus burada, oturum durumunda degil: hesap motorunun isi.
    resources: gathered.resources
      .map((resource) => {
        const base = resolveResourceMax(resource, level)
        if (base == null) return null
        const bonus = input.resourceBonuses?.[resource.id] ?? 0
        return { ...resource, max: Math.max(0, base + bonus) }
      })
      .filter((resource): resource is ResourceDef & { max: number } => resource !== null),
    size: choices.merged.size ?? race?.size ?? 'Orta',
    reach: (race?.reach ?? 5) + choices.merged.reachBonus,
    inventory: lines,
    weight: { total: weight, carryCapacity: carryCapacity(scores.str) },
    conditions,
    warnings,
  }
}

// SKILLS listesi types.ts'ten gelir (dongusel bagimlilik olmasin diye burada tekrar yazilmadi)
export { modText }
